// FE-01: what the app shows when rendering fails, instead of a white screen.
//
// - scope="workspace" (default): around each page in AppWorkspaceRouter, and
//   around the areas and frame parts App draws itself (AppArea below). A part
//   that throws shows a notice in its place, named after it. The rest of the
//   screen and everything App holds (e.g. an open manual review draft) stay,
//   because every boundary of this scope sits below App.
//   - Try again draws the part again.
//   - A new resetKey clears the notice: another page, or another item in it
//     (a risk module, a player, a case).
//   - renderFallback puts the notice in a frame: the Reports header and tabs,
//     over the page where a dialog would be, or where a floating notice sits.
//   - focus="whenLost": the notice takes focus only when focus was in the
//     part that failed. A frame part that fails while the person types in a
//     review leaves focus there. The alert role still announces it.
// - scope="app": around App in AppRoot. If App itself fails, its state is
//   gone, so the notice offers a reload. It styles itself: App's theme styles
//   (AppGlobalStyles) went with App.
// - Code that could not be loaded (a failed chunk, or a lazy page whose file
//   failed to load or run) gets Reload only. React.lazy keeps the failure, so
//   trying again would fail the same way. LazyWorkspaces reloads once by
//   itself after a deployment; this is a failure after that.
import { Component, createRef } from 'react';
import Icon from './Icon.js';
import { THEME_TOKENS, tokenBlock, uiTokenBlock } from './AppGlobalStyles.js';
import { isCodeLoadFailure } from './chunkErrors.js';
import { STATE_ACTIONS } from './uiVocabulary.js';
import './UiStates.css';
import './WorkspaceErrorBoundary.css';

export const RELOAD_APP = 'Reload the app';
export const KEPT_ELSEWHERE = 'The rest of the app still works, and work open elsewhere is kept. Try again, or reload the app.';

export function boundaryCopy({ scope = 'workspace', label = '', loadFailed = false, text = '' } = {}) {
  if (loadFailed) {
    return {
      title:'Part of the app could not be loaded.',
      text:scope === 'app' ? 'Check your connection, then reload.' : 'Check your connection and save any open work, then reload.',
    };
  }
  if (scope === 'app') return { title:'The app could not be shown.', text:'Reload to continue. Changes that were not saved may be lost.' };
  return { title:`${String(label || '').trim() || 'This page'} could not be shown.`, text:text || KEPT_ELSEWHERE };
}

// The app-level notice's own styles: the theme tokens App last set on <html>
// (data-theme), or the system's light or dark preference before App set one.
// Values come from AppGlobalStyles, so the notice matches the app.
const LAYOUT_TOKENS = "--radius:6px;--radius-lg:10px;--space-1:4px;--space-2:8px;--space-3:12px;--space-4:16px;--space-5:24px;--font-size-meta:12px;--font-size-body:14px;--font:'DM Sans',system-ui,sans-serif;";
const themeDeclarations = theme => `${tokenBlock(THEME_TOKENS[theme])}\n${uiTokenBlock(theme)}\n${LAYOUT_TOKENS}background-color:${THEME_TOKENS[theme].bg};color:${THEME_TOKENS[theme].text};`;
export function appBoundaryCss() {
  const page = '.workspace-boundary-page';
  return [
    `${page}{position:fixed;inset:0;overflow:auto;display:flex;align-items:flex-start;justify-content:center;padding:72px 16px 16px;font-family:var(--font);font-size:var(--font-size-body);line-height:1.5;${themeDeclarations('dark')}}`,
    `${page},${page} *{box-sizing:border-box}`,
    `${page} .workspace-boundary{width:100%;max-width:560px}`,
    `@media (prefers-color-scheme: light){html:not([data-theme]) ${page}{${themeDeclarations('light')}}}`,
    ...Object.keys(THEME_TOKENS).map(theme => `html[data-theme="${theme}"] ${page}{${themeDeclarations(theme)}}`),
  ].join('\n');
}

export class WorkspaceErrorBoundary extends Component {
  // Tests replace this; the app reloads the page.
  static reloadApp = () => { globalThis.location?.reload(); };

  constructor(props) {
    super(props);
    this.state = { error:null, resetKey:props.resetKey };
    this.notice = createRef();
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error:error || new Error('Unknown render error') };
  }

  // Another page, or another item in it, starts clean.
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey === state.resetKey) return null;
    return { resetKey:props.resetKey, error:null };
  }

  componentDidCatch(error, info) {
    this.props.onError?.(error, info);
  }

  // The notice takes focus when it appears, so keyboard and screen reader
  // users land on it. A page that renders normally keeps focus where it is.
  componentDidMount() {
    if (this.state.error) this.takeFocus();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.error && !prevState.error) this.takeFocus();
  }

  takeFocus() {
    const notice = this.notice.current;
    if (!notice) return;
    if (this.props.focus === 'whenLost') {
      // Focus left with the part that failed: the browser moved it to the body.
      const active = notice.ownerDocument.activeElement;
      if (active && active !== notice.ownerDocument.body && active.isConnected) return;
    }
    notice.focus();
  }

  // The page was unmounted while the notice showed, so clearing the error
  // draws it from scratch.
  retry() {
    this.setState({ error:null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const { label, text, onClose = null, closeLabel = 'Close', renderFallback = null } = this.props;
    const scope = this.props.scope === 'app' ? 'app' : 'workspace';
    const loadFailed = isCodeLoadFailure(error);
    const copy = boundaryCopy({ scope, label, loadFailed, text });
    const notice = (
      <div ref={this.notice} tabIndex={-1} className="ui-notice workspace-boundary" data-tone="red" data-scope={scope} role="alert">
        <Icon name="warning" size={16}/>
        <div className="ui-notice__body">
          <p className="ui-notice__text"><strong>{copy.title}</strong> {copy.text}</p>
          <div className="ui-state__actions">
            {scope === 'workspace' && !loadFailed
              ? <button type="button" className="ds-button ds-button--secondary ui-state__action" onClick={this.retry}>{STATE_ACTIONS.retry}</button>
              : null}
            <button type="button" className="ds-button ds-button--secondary ui-state__action" onClick={() => WorkspaceErrorBoundary.reloadApp()}>{RELOAD_APP}</button>
            {onClose ? <button type="button" className="ds-button ds-button--secondary ui-state__action" onClick={onClose}>{closeLabel}</button> : null}
          </div>
        </div>
      </div>
    );
    if (scope === 'app') return <div className="workspace-boundary-page"><style>{appBoundaryCss()}</style>{notice}</div>;
    return typeof renderFallback === 'function' ? renderFallback(notice) : notice;
  }
}

// The areas App draws itself, outside AppWorkspaceRouter, and the parts of
// App's frame. Each fails into its own notice below App, so App's state stays,
// including an open manual review with its unsaved answers.
const REVIEW_KEPT = 'Any review you have open, with its unsaved changes, is kept while the app stays open.';
const DIALOG_KEPT = 'The rest of the app still works, and work open elsewhere is kept. Try again, close it, or reload the app.';
// A dialog's notice sits where the dialog would be, over the page, and closes
// like the dialog (its close button or Escape).
const overDialog = (onClose, area) => notice => <div className="workspace-boundary-overlay" data-area={area} onKeyDown={event => { if (event.key === 'Escape') onClose?.(); }}>
  <div className="workspace-boundary-overlay__panel">{notice}</div>
</div>;
// A floating part (toasts, the session warning) keeps its notice where it floats.
const floating = place => () => notice => <div className={`workspace-boundary-floating workspace-boundary-floating--${place}`}>{notice}</div>;
// A frame part that fails while the person works elsewhere leaves focus there.
const FRAME = { text:KEPT_ELSEWHERE, focus:'whenLost' };
const APP_AREAS = {
  manualReview:{ label:() => 'Manual Review', text:`${REVIEW_KEPT} Try again to show it.` },
  section:{ label:title => (title && title !== 'Menu' ? `The ${title} menu` : 'This menu'), text:KEPT_ELSEWHERE },
  search:{
    label:() => 'Search',
    text:'The rest of the app still works, and work open elsewhere is kept. Try again, close search, or reload the app.',
    closeLabel:'Close search',
    frame:overDialog,
  },
  templatePicker:{ label:() => 'The template choice', text:`${REVIEW_KEPT} Try again, or close it.`, closeLabel:'Close', frame:overDialog },
  // App's frame: the rail (or the phone navigation), the top bar, the
  // automation notice, toasts and the session warning.
  navigation:{ ...FRAME, label:() => 'Navigation' },
  topBar:{ ...FRAME, label:() => 'The top bar' },
  readiness:{ ...FRAME, label:() => 'The automation status' },
  toasts:{ ...FRAME, label:() => 'A notification', frame:floating('corner') },
  sessionNotice:{ ...FRAME, label:() => 'The session warning', text:'Your session ends soon, so save open work. Try again, or reload the app.', frame:floating('top') },
  // The shell's dialogs and the phone section sheet.
  pinnedPages:{ label:() => 'Pinned pages', text:DIALOG_KEPT, closeLabel:'Close', frame:overDialog },
  shortcuts:{ label:() => 'Keyboard shortcuts', text:DIALOG_KEPT, closeLabel:'Close', frame:overDialog },
  sectionSheet:{ label:() => 'The section menu', text:DIALOG_KEPT, closeLabel:'Close', frame:overDialog },
  // The question asked before leaving unsaved changes. Closing it stays.
  leave:{
    label:() => 'The question about unsaved changes',
    text:'Your unsaved changes are kept. Try again, stay on this page, or reload the app.',
    closeLabel:'Stay on this page',
    frame:overDialog,
  },
};

export function AppArea({ area, resetKey = '', label = '', onClose = null, children }) {
  const definition = APP_AREAS[area] || APP_AREAS.section;
  return <WorkspaceErrorBoundary resetKey={`${area}:${resetKey}`} label={definition.label(label)} text={definition.text} focus={definition.focus}
    onClose={onClose} closeLabel={definition.closeLabel} renderFallback={definition.frame ? definition.frame(onClose, area) : null}>
    {children}
  </WorkspaceErrorBoundary>;
}

export default WorkspaceErrorBoundary;
